import { Component, ChangeDetectionStrategy, inject, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import {
  heroCheckCircle,
  heroXCircle,
  heroExclamationTriangle,
  heroInformationCircle,
  heroXMark,
  heroMagnifyingGlass,
  heroHandThumbUp,
  heroHandThumbDown,
  heroFlag
} from '@ng-icons/heroicons/outline';
import { ToastService } from '../../services/toast.service';
import { Toast } from '../../interfaces/toast.interface';

@Component({
  selector: 'app-toast',
  imports: [CommonModule, RouterModule, NgIconComponent],
  providers: [
    provideIcons({
      heroCheckCircle,
      heroXCircle,
      heroExclamationTriangle,
      heroInformationCircle,
      heroXMark,
      heroMagnifyingGlass,
      heroHandThumbUp,
      heroHandThumbDown,
      heroFlag
    })
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="toast toast-end toast-top">
      @for (toast of toasts(); track toast.id) {
        <div 
          class="alert toast-item animate-slide-in-right"
          [class]="getAlertClass(toast.type)"
        >
          @if (toast.icon) {
            <ng-icon 
              [name]="toast.icon" 
              size="20"
              class="flex-shrink-0"
            />
          }
          <div class="flex-1">
            <div class="text-sm font-medium">{{ toast.message }}</div>
            @if (toast.action) {
              <div class="mt-2">
                @if (toast.action.route) {
                  <a 
                    [routerLink]="toast.action.route"
                    class="btn btn-xs btn-ghost"
                    (click)="toast.action.onClick?.(); dismiss(toast.id)"
                  >
                    {{ toast.action.label }} →
                  </a>
                } @else {
                  <button 
                    type="button"
                    class="btn btn-xs btn-ghost"
                    (click)="toast.action.onClick?.(); dismiss(toast.id)"
                  >
                    {{ toast.action.label }} →
                  </button>
                }
              </div>
            }
          </div>
          @if (toast.dismissible) {
            <button 
              type="button"
              class="btn btn-ghost btn-xs p-1 min-h-0 h-auto"
              (click)="dismiss(toast.id)"
            >
              <ng-icon name="heroXMark" size="16" />
            </button>
          }
        </div>
      }
    </div>
  `,
  styles: [`
    :host {
      position: fixed;
      top: 0;
      right: 0;
      z-index: 9999;
    }

    .toast-item {
      margin-bottom: 0.5rem;
      min-width: 300px;
      animation-duration: 0.3s;
      animation-fill-mode: both;
    }

    .animate-slide-in-right {
      animation-name: slideInRight;
    }

    @keyframes slideInRight {
      from {
        opacity: 0;
        transform: translateX(100%);
      }
      to {
        opacity: 1;
        transform: translateX(0);
      }
    }

    .toast-item.removing {
      animation: slideOutRight 0.3s ease-out forwards;
    }

    @keyframes slideOutRight {
      from {
        opacity: 1;
        transform: translateX(0);
      }
      to {
        opacity: 0;
        transform: translateX(100%);
      }
    }

    @media (max-width: 768px) {
      .toast-item {
        min-width: 280px;
        margin-left: 1rem;
        margin-right: 1rem;
      }
    }
  `]
})
export class ToastComponent {
  private toastService = inject(ToastService);
  
  toasts = this.toastService.toasts;

  getAlertClass(type: Toast['type']): string {
    switch (type) {
      case 'success':
        return 'alert-success';
      case 'error':
        return 'alert-error';
      case 'warning':
        return 'alert-warning';
      case 'info':
      default:
        return 'alert-info';
    }
  }

  dismiss(id: string): void {
    this.toastService.dismiss(id);
  }
}